import React from 'react';
import { FilterIcon, XmarkIcon } from '@revfluence/fresh-icons/solid';
import { DismissableBadge } from '../../../components/filters/badges/dismissable-badge';
import { AdAccountBadges } from './ad-account-badges';
import { AdCampaignBadges } from './ad-campaign-badges';
import { AdObjectiveBadges } from './ad-objective-badges';
import { MemberBadges } from './member-badges';
import { PartnershipAdsBadge } from './partnership-ads-badge';
import {
  AdAccountBadgeHookResult,
  AdCampaignBadgeHookResult,
  AdObjectiveBadgeHookResult,
  MemberBadgeHookResult,
  PartnershipAdsBadgeHookResult,
} from './types';

interface SocialAdsFilterBadgesProps {
  adAccountBadgesProps: AdAccountBadgeHookResult['adAccountBadgesProps'];
  adCampaignBadgesProps: AdCampaignBadgeHookResult['adCampaignBadgesProps'];
  adObjectiveBadgesProps: AdObjectiveBadgeHookResult['adObjectiveBadgesProps'];
  memberBadgesProps: MemberBadgeHookResult['memberBadgesProps'];
  partnershipAdsBadgeProps: PartnershipAdsBadgeHookResult['partnershipAdsBadgeProps'];
  onOpenFilters: () => void;
  onRemoveFilters: (type: string) => (event: React.MouseEvent<HTMLButtonElement>) => void;
  onClearAllFilters: () => void;
}

export const SocialAdsFilterBadges: React.FC<SocialAdsFilterBadgesProps> = ({
  adAccountBadgesProps,
  adCampaignBadgesProps,
  adObjectiveBadgesProps,
  memberBadgesProps,
  partnershipAdsBadgeProps,
  onOpenFilters,
  onRemoveFilters,
  onClearAllFilters,
}) => {
  // Check if there are any individual filter badges to display
  const hasIndividualBadges =
    adAccountBadgesProps.data.length > 0 ||
    adCampaignBadgesProps.data.campaigns.length > 0 ||
    adCampaignBadgesProps.data.adSets.length > 0 ||
    adObjectiveBadgesProps.data.length > 0 ||
    memberBadgesProps.data.length > 0 ||
    partnershipAdsBadgeProps.data.length > 0;

  // Show "Filters" badge when no individual badges are present
  if (!hasIndividualBadges) {
    return (
      <DismissableBadge
        variant="secondary"
        className="mr-2 mb-2"
        aria-label="Filters"
        role="button"
        data-dd-action-name="open-ads-filters"
        onClick={onOpenFilters}
      >
        <FilterIcon className="mr-2" />
        Filters
      </DismissableBadge>
    );
  }

  // Show individual filter badges and "Clear all" badge when there are individual badges
  return (
    <>
      <AdAccountBadges {...adAccountBadgesProps} onClick={onOpenFilters} onDismiss={onRemoveFilters('adAccountIds')} />
      <AdCampaignBadges
        {...adCampaignBadgesProps}
        onClick={onOpenFilters}
        onDismiss={onRemoveFilters('adCampaignAndSetIds')}
      />
      <AdObjectiveBadges
        {...adObjectiveBadgesProps}
        onClick={onOpenFilters}
        onDismiss={onRemoveFilters('adObjectiveIds')}
      />
      <MemberBadges {...memberBadgesProps} onClick={onOpenFilters} onDismiss={onRemoveFilters('memberIds')} />
      <PartnershipAdsBadge
        {...partnershipAdsBadgeProps}
        onClick={onOpenFilters}
        onDismiss={onRemoveFilters('compareWithPartnershipAds')}
      />

      {/* Clear all filters badge */}
      <DismissableBadge
        variant="secondary"
        className="mr-2 mb-2"
        aria-label="Clear all filters"
        role="button"
        onClick={onClearAllFilters}
        data-dd-action-name="clear-all-ads-filters"
      >
        Clear all
        <XmarkIcon className="ml-2" />
      </DismissableBadge>
    </>
  );
};

SocialAdsFilterBadges.displayName = 'SocialAdsFilterBadges';
